import { type Scene } from '@/designs-dev/scenes';
import { type ShortlistInitial } from '@/cam-admin/paywall/useShortlists';

// Every state the CAM shortlist prototype can start in, paired with the props
// that put it there. One source of truth for the live state select on the stage
// and for the stories, so the two can't drift.
//
// The states are the paywall lifecycle, in the order a real shortlist moves
// through it — then the two ways it goes wrong. Publishing is what sends, so the
// lifecycle is one action and then a wait: a draft nobody has been told about, the
// list live with every link out, then the payments arriving one at a time.
export const shortlistScenes: Scene<ShortlistInitial>[] = [
  {
    id: 'fresh',
    label: 'Draft, nothing sent',
    kind: 'happy',
    repro: 'List assembled, nothing sent. One button on it: Publish and send.',
    initial: { scenario: 'fresh' },
  },
  {
    id: 'some-sent',
    label: 'One added since the send',
    kind: 'happy',
    repro: 'Sent to three, then a fourth added. Only that row has a send left.',
    initial: { scenario: 'someSent' },
  },
  {
    id: 'first-paid',
    label: 'First payment in',
    kind: 'happy',
    repro: 'Everyone messaged, one provider paid.',
    initial: { scenario: 'firstPaid' },
  },
  {
    id: 'published',
    label: 'Published to the family',
    kind: 'happy',
    repro:
      'Published, so the links have gone out. The provider who paid is Visible; everyone else is Hidden until they do.',
    initial: { scenario: 'firstPaid', published: true },
  },
  {
    id: 'care-home-list',
    label: 'Care home list — no paywall',
    kind: 'view',
    // The other half of a care circle's search, and the comparison the tab is
    // making: same table, paywall taken out. The paywall is dom only because a
    // dom provider will pay for a lead and a care home is rung up by a CAM.
    repro:
      'The care home list, on the same tab. Publish rather than Publish and send, the CAM’s own statuses, no Visibility column and nothing to send on a row.',
    initial: { scenario: 'fresh', shortlistId: 'sl-jun-18' },
  },
  {
    id: 'publish-homes',
    label: 'Publishing a care home list',
    kind: 'happy',
    // The warning is the scene. On a dom list publishing shows the family
    // nobody until somebody pays; here it shows them everybody, and a CAM
    // switching between the two lists is who this sentence is for.
    repro:
      'The confirm, on the care home draft. Nothing is sent, and every home is in front of the family the moment it lands.',
    initial: {
      scenario: 'fresh',
      shortlistId: 'sl-jun-18',
      homePublishOpen: true,
    },
  },
  {
    id: 'publish-send',
    label: 'Publish and send',
    kind: 'happy',
    repro:
      'The modal, on a draft. The request pulled from the care assessment, editable, with the count of who it reaches.',
    initial: { scenario: 'fresh', publishOpen: true },
  },
  {
    id: 'send-preview',
    label: 'Previewing the send',
    kind: 'happy',
    repro:
      'What the first provider on the list will get, in their own mail client, before it goes.',
    initial: { scenario: 'fresh', previewOpen: true },
  },
  {
    id: 'hidden-unpaid',
    label: 'Published, nobody has paid',
    kind: 'edge',
    // The likely first hour of a published list, and the case the override
    // exists for: the family is looking at an empty shortlist while five
    // providers decide.
    repro:
      'Every link out, nothing back. Every row Hidden, and Make visible is the only way one moves.',
    initial: { scenario: 'allQuiet', published: true },
  },
  {
    id: 'all-quiet',
    label: 'Nobody has answered',
    kind: 'empty',
    // The real empty state, and the likely one. Declining takes a deliberate tap
    // on a link a provider has no reason to open, so the way a shortlist fails is
    // silence rather than refusal.
    repro: 'All five have the request, none have answered.',
    initial: { scenario: 'allQuiet' },
  },
  {
    id: 'cam-entered',
    label: 'Typed up from a phone call',
    kind: 'happy',
    repro:
      'A quiet list, open on the row a colleague typed up from a phone call.',
    initial: { scenario: 'allQuiet', openRow: 'riverside-lodge' },
  },
  {
    id: 'cam-entry-empty',
    label: 'Nothing on a row yet',
    kind: 'empty',
    repro:
      'The same list, open on a row nobody has answered. No answers block.',
    initial: { scenario: 'allQuiet', openRow: 'bramble-court' },
  },
  {
    id: 'one-declined',
    label: 'One no, four nothings',
    kind: 'happy',
    repro: 'One provider tapped the no. The other four are silent.',
    initial: { scenario: 'oneDeclined' },
  },
];
